import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useModuleAssignments,
  useCreateAssignment,
  useDeleteAssignment,
  useUpdateAssignment,
} from "@/hooks/useAssignments";
import { AssignmentBuilder } from "./AssignmentBuilder";
import { Plus, Trash2, ChevronDown, ChevronRight, FileText, Loader2 } from "lucide-react";
import type { AssignmentCompletionRequirement } from "@/lib/assignmentGating";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AssignmentManagerProps {
  moduleId: string;
}

const COMPLETION_OPTIONS: {
  value: AssignmentCompletionRequirement;
  label: string;
}[] = [
  { value: "review_required", label: "Instructor review required to continue" },
  {
    value: "submission_required",
    label: "Assignment required, instructor review not required to continue",
  },
  { value: "optional", label: "Assignment presented but not required" },
];

const HELPER_TEXT =
  'Choosing "Assignment required, instructor review not required to continue" unlocks the next module as soon as the learner submits, even before you review it.';

function tierBadge(tier: AssignmentCompletionRequirement) {
  if (tier === "review_required") {
    return (
      <Badge variant="outline" className="text-orange-600 border-orange-300">
        Review required
      </Badge>
    );
  }
  if (tier === "submission_required") {
    return (
      <Badge variant="outline" className="text-blue-600 border-blue-300">
        Submission required
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Optional
    </Badge>
  );
}

export function AssignmentManager({ moduleId }: AssignmentManagerProps) {
  const { data: assignments, isLoading } = useModuleAssignments(moduleId);
  const createAssignment = useCreateAssignment();
  const deleteAssignment = useDeleteAssignment();
  const updateAssignment = useUpdateAssignment();

  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newRequirement, setNewRequirement] =
    useState<AssignmentCompletionRequirement>("review_required");
  const [expandedAssignment, setExpandedAssignment] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const handleCreate = () => {
    if (!newTitle.trim()) return;
    createAssignment.mutate(
      {
        module_id: moduleId,
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
        completion_requirement: newRequirement,
      },
      {
        onSuccess: () => {
          setNewTitle("");
          setNewDescription("");
          setNewRequirement("review_required");
          setShowForm(false);
        },
      }
    );
  };

  const togglePublish = (id: string, currentValue: boolean) => {
    updateAssignment.mutate({ id, is_published: !currentValue });
  };

  const setRequirement = (
    id: string,
    value: AssignmentCompletionRequirement
  ) => {
    updateAssignment.mutate({ id, completion_requirement: value });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-muted-foreground">Assignments</h4>
        {!showForm && (
          <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Assignment
          </Button>
        )}
      </div>

      {showForm && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">New Assignment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="assignment-title">Title</Label>
              <Input
                id="assignment-title"
                placeholder="Assignment title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="assignment-description">Description (optional)</Label>
              <Textarea
                id="assignment-description"
                placeholder="Describe the assignment..."
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-assignment-requirement">Completion requirement</Label>
              <Select
                value={newRequirement}
                onValueChange={(v) =>
                  setNewRequirement(v as AssignmentCompletionRequirement)
                }
              >
                <SelectTrigger id="new-assignment-requirement">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPLETION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{HELPER_TEXT}</p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={!newTitle.trim() || createAssignment.isPending}
              >
                {createAssignment.isPending && (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                )}
                Create
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setNewTitle("");
                  setNewDescription("");
                  setNewRequirement("review_required");
                }}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {assignments?.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground text-center py-4">
          No assignments yet. Create one to get started.
        </p>
      )}

      <div className="space-y-2">
        {assignments?.map((assignment) => (
          <Card key={assignment.id} className="overflow-hidden">
            <div
              className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50"
              onClick={() =>
                setExpandedAssignment(
                  expandedAssignment === assignment.id ? null : assignment.id
                )
              }
            >
              <div className="flex items-center gap-3">
                {expandedAssignment === assignment.id ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <FileText className="h-4 w-4 text-primary" />
                <span className="font-medium">{assignment.title}</span>
                {tierBadge(assignment.completion_requirement)}
                <Badge variant={assignment.is_published ? "default" : "secondary"}>
                  {assignment.is_published ? "Published" : "Draft"}
                </Badge>
              </div>
              <div
                className="flex items-center gap-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor={`req-${assignment.id}`}
                    className="text-xs text-muted-foreground"
                  >
                    Completion
                  </Label>
                  <Select
                    value={assignment.completion_requirement}
                    onValueChange={(v) =>
                      setRequirement(
                        assignment.id,
                        v as AssignmentCompletionRequirement
                      )
                    }
                  >
                    <SelectTrigger
                      id={`req-${assignment.id}`}
                      className="h-8 w-[220px] text-xs"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPLETION_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor={`publish-${assignment.id}`}
                    className="text-xs text-muted-foreground"
                  >
                    Published
                  </Label>
                  <Switch
                    id={`publish-${assignment.id}`}
                    checked={assignment.is_published}
                    onCheckedChange={() =>
                      togglePublish(assignment.id, assignment.is_published)
                    }
                  />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setDeleteId(assignment.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {expandedAssignment === assignment.id && (
              <div className="border-t p-4">
                {assignment.description && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {assignment.description}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mb-4">{HELPER_TEXT}</p>
                <AssignmentBuilder assignmentId={assignment.id} />
              </div>
            )}
          </Card>
        ))}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this assignment and all its questions. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteId) {
                  deleteAssignment.mutate(deleteId);
                  setDeleteId(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
